<template>
  <div class="rt-card">
    <header class="rt-header">
      <div>
        <h5 class="rt-title">Fluxo de check-ins</h5>
        <p class="rt-subtitle">
          <span class="rt-pin rt-pin-in"></span>
          Entradas
          <template v-if="showCheckout">
            <span class="rt-pin rt-pin-out"></span>
            Saídas
          </template>
          <span v-if="peakLabel" class="rt-peak">
            <i class="bx bx-trending-up"></i>
            Pico {{ peakLabel }}
          </span>
        </p>
      </div>
      <div class="rt-bucket-chips">
        <button
          v-for="opt in bucketOptions"
          :key="opt.value"
          type="button"
          class="rt-bchip"
          :class="{ 'rt-bchip-active': bucket === opt.value }"
          @click="$emit('change-bucket', opt.value)"
        >
          {{ opt.label }}
        </button>
      </div>
    </header>

    <div class="rt-chart-wrap">
      <div v-if="isEmpty" class="rt-empty">
        <i class="bx bx-line-chart-down rt-empty-icon"></i>
        <p>Nenhum movimento no período selecionado</p>
        <small>Os dados aparecem em tempo real conforme acontecem leituras</small>
      </div>
      <apexchart
        v-else
        type="area"
        :options="chartOptions"
        :series="chartSeries"
        height="320"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: 'river-timeline',
  props: {
    timeline: { type: Array, default: () => [] },
    bucket: { type: String, default: '5min' },
    peakHour: { type: String, default: null },
    showCheckout: { type: Boolean, default: true }
  },
  data: () => ({
    bucketOptions: [
      { value: '1min', label: '1m' },
      { value: '5min', label: '5m' },
      { value: '15min', label: '15m' },
      { value: '1h', label: '1h' },
      { value: '1d', label: '1d' }
    ]
  }),
  computed: {
    isEmpty () {
      if (!this.timeline || this.timeline.length === 0) return true
      return !this.timeline.some(b => (b.in || 0) > 0 || (b.out || 0) > 0)
    },
    peakLabel () {
      return this.peakHour || null
    },
    chartSeries () {
      const ins = {
        name: 'Entradas',
        data: this.timeline.map(b => [new Date(b.bucket).getTime(), b.in || 0])
      }
      if (!this.showCheckout) return [ins]
      // Saídas como NEGATIVOS — efeito "rio" abaixo do eixo
      return [
        ins,
        {
          name: 'Saídas',
          data: this.timeline.map(b => [new Date(b.bucket).getTime(), -(b.out || 0)])
        }
      ]
    },
    chartOptions () {
      return {
        chart: {
          type: 'area',
          stacked: false,
          toolbar: { show: false },
          zoom: { enabled: false },
          animations: { enabled: true, easing: 'easeinout', speed: 400 },
          fontFamily: 'inherit'
        },
        colors: ['#34c38f', '#f1b44c'],
        dataLabels: { enabled: false },
        stroke: {
          curve: 'smooth',
          width: 2.5,
          lineCap: 'round'
        },
        fill: {
          type: 'gradient',
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.45,
            opacityTo: 0.05,
            stops: [0, 90, 100]
          }
        },
        markers: {
          size: 0,
          hover: { size: 5 }
        },
        xaxis: {
          type: 'datetime',
          labels: {
            datetimeUTC: false,
            style: { colors: '#8b90a5', fontSize: '11px' }
          },
          axisBorder: { show: false },
          axisTicks: { color: '#eaecf2' }
        },
        yaxis: {
          labels: {
            style: { colors: '#8b90a5', fontSize: '11px' },
            formatter: (val) => Math.abs(Math.round(val))
          }
        },
        grid: {
          borderColor: '#eaecf2',
          strokeDashArray: 4,
          xaxis: { lines: { show: false } }
        },
        legend: { show: false },
        tooltip: {
          theme: 'light',
          x: { format: this.tooltipDateFormat() },
          y: {
            formatter: (val) => `${Math.abs(val)} ${val >= 0 ? 'entradas' : 'saídas'}`
          }
        },
        annotations: this.peakAnnotations()
      }
    }
  },
  methods: {
    tooltipDateFormat () {
      switch (this.bucket) {
        case '1min':
        case '5min':
        case '15min': return 'dd/MM HH:mm'
        case '1h': return 'dd/MM HH:00'
        case '1d': return 'dd/MM/yyyy'
        default: return 'dd/MM HH:mm'
      }
    },
    /**
     * Anotação visual no momento do pico — linha pontilhada vertical com
     * label discreto. Só pinta se conseguirmos cruzar o peakHour com algum
     * bucket existente.
     */
    peakAnnotations () {
      if (!this.peakHour || !this.timeline.length) return {}
      const targetHour = parseInt(this.peakHour.split(':')[0], 10)
      // Acha o bucket com maior `in` dentro daquela hora
      let bestBucket = null
      let bestVal = -1
      for (const b of this.timeline) {
        const d = new Date(b.bucket)
        if (d.getHours() === targetHour && (b.in || 0) > bestVal) {
          bestVal = b.in
          bestBucket = b
        }
      }
      if (!bestBucket) return {}
      return {
        xaxis: [{
          x: new Date(bestBucket.bucket).getTime(),
          borderColor: '#8b5cf6',
          strokeDashArray: 5,
          label: {
            borderColor: '#8b5cf6',
            style: {
              color: '#fff',
              background: '#8b5cf6',
              fontSize: '10px',
              fontWeight: 600
            },
            text: 'Pico'
          }
        }]
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.rt-card {
  background: #fff;
  border: 1px solid #eaecf2;
  border-radius: 5px;
  padding: 22px 24px;
  box-shadow: 0 1px 3px rgba(26, 29, 38, 0.04);
  transition: box-shadow 0.2s ease, border-color 0.2s ease;

  &:hover {
    border-color: #d4d8e3;
    box-shadow: 0 4px 16px rgba(26, 29, 38, 0.06);
  }
}

.rt-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 10px;
  flex-wrap: wrap;
}

.rt-title {
  font-size: 14px;
  font-weight: 700;
  color: #1a1d26;
  margin: 0 0 4px 0;
}

.rt-subtitle {
  font-size: 12px;
  color: #8b90a5;
  margin: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.rt-pin {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  &.rt-pin-in  { background: #34c38f; }
  &.rt-pin-out { background: #f1b44c; }
}
.rt-pin-out { margin-left: 8px; }

.rt-peak {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 12px;
  padding: 2px 8px;
  background: rgba(139, 92, 246, 0.08);
  color: #8b5cf6;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;

  i { font-size: 12px; }
}

.rt-bucket-chips {
  display: inline-flex;
  background: #f5f6fa;
  border: 1px solid #eaecf2;
  border-radius: 8px;
  padding: 2px;
  gap: 1px;
}

.rt-bchip {
  background: transparent;
  border: 0;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 600;
  color: #4a5068;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.12s ease;
  letter-spacing: 0.02em;

  &:hover { background: #fff; }

  &.rt-bchip-active {
    background: #fff;
    color: #556ee6;
    box-shadow: 0 1px 3px rgba(26, 29, 38, 0.08);
  }
}

.rt-chart-wrap {
  min-height: 320px;
  position: relative;
}

.rt-empty {
  height: 320px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #8b90a5;

  .rt-empty-icon {
    font-size: 42px;
    color: #d0d5dd;
    margin-bottom: 8px;
  }

  p { font-size: 13px; font-weight: 600; margin: 0 0 4px 0; color: #4a5068; }
  small { font-size: 12px; }
}
</style>
